import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@mui/material";
import { useSnackbar } from "notistack";
import { Empty } from "antd";
import { DateConverter } from "../../../utils/DateConverter";
import { getWorkflowById } from "../../../api's/WorkflowApi";
import { getDataProcessor, getNodeStatus } from "../../../api's/DataProcessorApi";
import LoadingIcon from "../../../reusable-component/LoadingIcon";
import { addRecentVisitWorkflowsApi, getRecentVisitWorkflowsApi } from "../../../api's/DashboardApi";

const CheckIcon = () => (
    <div style={{
        width: 22, height: 22, borderRadius: "50%",
        background: "#e8f8f2", display: "flex",
        alignItems: "center", justifyContent: "center",
        flexShrink: 0, marginTop: 1,
    }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
            stroke="#1a9e6e" strokeWidth="3">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    </div>
);

const ActivityItem = ({ workflowId, workflowName, createdAt, createdBy, isLast, navigateToWorkflow }) => (
    <div style={{
        display: "flex", gap: 12, padding: "13px 0",
        borderBottom: isLast ? "none" : "0.5px solid rgba(0,0,0,0.08)",
        alignItems: "flex-start",
    }}>
        <CheckIcon />
        <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#20639B" }}>
                    <button onClick={() => navigateToWorkflow(workflowId)} style={{
                        fontSize: 14, fontWeight: 600, color: "#20639B",
                        background: "none", border: "none", cursor: "pointer", padding: 0,
                    }}>
                        {workflowName}
                    </button>
                </span>
                <span style={{ color: "#ccc", fontSize: 12 }}>|</span>
                <span style={{ fontSize: 12.5, color: "#6b7f93" }}>{DateConverter(createdAt)}</span>
            </div>
            <div style={{ fontSize: 12.5, color: "#6b7f93", marginTop: 3 }}>
                Created by{" "}
                <strong style={{ fontWeight: 600, color: "#333" }}>{createdBy}</strong>
            </div>
        </div>
    </div>
);

export default function RecentVisitWorkflows() {
    const navigate = useNavigate();
    const [recentVisitWorkflows, setRecentVisitWorkflows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingWorkflow, setLoadingWorkflow] = useState(false);
    const { enqueueSnackbar } = useSnackbar();


    const fetchRecentVisitWorkflows = async () => {
        setLoading(true);
        try {
            const response = await getRecentVisitWorkflowsApi();
            if (response.status === 200) {
                setRecentVisitWorkflows(response.data);
            }
        } catch (error) {
            setRecentVisitWorkflows([]);
            if (error.response?.status !== 404) {
                enqueueSnackbar('Error while fetching recent visit workflows!', { variant: 'error' });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRecentVisitWorkflows();
    }, []);

    const handleAddRecentVisitWorkflow = async (workflow_id) => {
        try {
            await addRecentVisitWorkflowsApi(workflow_id);
        } catch (error) {
            console.log(error);
            enqueueSnackbar('Error while adding recent visit workflow!', { variant: 'error' });
        }
    };


    const navigateToWorkflow = async (workflow_id) => {
        sessionStorage.removeItem('allNodes');
        sessionStorage.removeItem('node');
        sessionStorage.removeItem('elementCount');
        sessionStorage.removeItem('saved_node');
        sessionStorage.removeItem('edges');
        setLoadingWorkflow(true);

        let result, nodeStatus;
        try {
            const response = await getWorkflowById(workflow_id);
            result = await getDataProcessor(workflow_id, response.data.execution_mode);
            nodeStatus = await getNodeStatus(
                response.data.engagement.client.client_name,
                response.data.engagement.engagement_name,
                response.data.workflow_name,
                response.data.cloudEnvironment
            );
            sessionStorage.setItem('nodeStatus', JSON.stringify(nodeStatus.data));
            if (response.status === 200) {
                if (result) {
                    let elemCount = 0;
                    const newSavedElement = [];

                    result.data.nodes.forEach((el) => {
                        el['nodeId'] = `${el.nodeId}`;
                        elemCount++;
                        newSavedElement.push(el.id);
                    });

                    sessionStorage.setItem('allNodes', JSON.stringify(result.data.form_data));
                    sessionStorage.setItem('elementCount', elemCount);
                    sessionStorage.setItem('node', JSON.stringify(result.data.nodes));
                    sessionStorage.setItem('edges', JSON.stringify(result.data.edges));
                    sessionStorage.setItem('saved_node', JSON.stringify(newSavedElement));
                }

                const type =
                    response.data?.process_type === 'Data Quality'
                        ? 'data-quality'
                        : response.data?.process_type === 'Data Copy'
                            ? 'data-copy'
                            : response.data?.process_type === 'Data Transformation'
                                ? 'data-transformation'
                                : response.data?.process_type === 'Reporting'
                                    ? 'reporting'
                                    : response.data?.process_type === 'Data Ingestion'
                                        ? 'ingestion'
                                        : response.data?.process_type === 'Modelling & Analytics'
                                            ? 'modelling-analytics'
                                            : response.data?.process_type === 'XPerience360'
                                                ? 'xperience360'
                                                : response.data?.process_type === 'Customer Segmentation'
                                                    ? 'customer-segmentation'
                                                    : response.data?.process_type === 'Agentic Streams'
                                                        ? 'agentic-streams'
                                                        : 'data';

                navigate(`/workflow-${type}/onboarding`, {
                    state: {
                        workflow_name: response.data?.workflow_name,
                        client_name: response.data?.engagement.client.client_name,
                        engagement_name: response.data?.engagement.engagement_name,
                        execution_mode: response.data?.execution_mode,
                        created_by: response.data?.user_name,
                        created_at: response.data?.creation_date,
                        status: response.data?.status,
                        workflow_data: response.data,
                        history: "dashboard"
                    },
                });
                handleAddRecentVisitWorkflow(workflow_id);
            }
        } catch (error) {
            console.log(error);
            sessionStorage.setItem('nodeStatus', JSON.stringify([]));
            if (error.response?.status === 409) {
                enqueueSnackbar('Access denied!', { variant: 'error' });
            }
        } finally {
            setLoadingWorkflow(false);
        }
    };

    return (
        <>
            <style>{`
                .workflows-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                .workflows-scroll::-webkit-scrollbar-track {
                    background: transparent;
                }
                .workflows-scroll::-webkit-scrollbar-thumb {
                    background-color: rgba(0, 0, 0, 0.15);
                    border-radius: 3px;
                }
                .workflows-scroll::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(0, 0, 0, 0.25);
                }
            `}</style>
            <div style={{
                background: "#fff",
                borderRadius: 4,
                padding: "20px 24px",
                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
                userSelect: "none",
                // height: "100%",
                display: "flex",
                flexDirection: "column",
                width: "100%",
            }}>
                <div style={{
                    display: "flex", alignItems: "center",
                    justifyContent: "space-between", marginBottom: 18,
                }}>
                    <span style={{
                        fontSize: 17,
                        fontWeight: 700,
                        color: 'var(--third-color)',
                        letterSpacing: "-0.2px",
                    }}>
                        Recent Visit Workflows
                    </span>
                    <button onClick={() => navigate("/workflow-data")} style={{
                        fontSize: 13, fontWeight: 500, color: "#20639B",
                        background: "none", border: "none", cursor: "pointer", padding: 0,
                    }}>
                        View All Workflows
                    </button>
                </div>

                <div className="workflows-scroll" style={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    minHeight: 0,
                    maxHeight: 300,
                }}>
                    {loading && recentVisitWorkflows.length === 0 && <div>
                        <Skeleton variant="rounded" width="100%" height={40} style={{ marginBottom: 10, paddingRight: 10 }} />
                        <Skeleton variant="rounded" width="100%" height={40} style={{ marginBottom: 10, paddingRight: 10 }} />
                        <Skeleton variant="rounded" width="100%" height={40} style={{ marginBottom: 10, paddingRight: 10 }} />
                    </div>}
                    {recentVisitWorkflows.length > 0 && recentVisitWorkflows?.map((item, index) => (
                        <ActivityItem
                            key={item.workflowId}
                            {...item}
                            isLast={index === recentVisitWorkflows.length - 1}
                            navigateToWorkflow={navigateToWorkflow}
                        />
                    ))}
                    {recentVisitWorkflows.length === 0 && <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "90%" }}>
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recent visit workflows found" />
                    </div>}
                </div>
            </div>
            {loadingWorkflow && <LoadingIcon />}
        </>
    );
}